import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Search, CheckCheck, Archive, ArchiveRestore, FileSpreadsheet, ExternalLink, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { notifModule, PRIORITY_STYLES, PRIORITY_ORDER } from "@/lib/notifModules";

const STATUS_STYLES = {
  new: "bg-rose-50 text-rose-700 border-rose-200",
  read: "bg-slate-50 text-slate-500 border-slate-200",
  actioned: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

// Stable DB `type` values per module (comma list = group, trailing * = prefix match)
const TYPE_OPTIONS = {
  employees: [
    ["cert_missing_health", "ntype_cert_missing_health"],
    ["cert_expired_health", "ntype_cert_expired_health"],
    ["cert_expiring_health", "ntype_cert_expiring_health"],
    ["cert_missing_training", "ntype_cert_missing_training"],
    ["cert_expired_training", "ntype_cert_expired_training"],
    ["cert_expiring_training", "ntype_cert_expiring_training"],
    ["no_municipality_card", "ntype_no_municipality_card"],
    ["cert_missing_health,cert_missing_training,no_municipality_card", "ntype_missing_data"],
    ["transfer_request", "ntype_transfer_request"],
    ["transfer_result", "ntype_transfer_result"],
  ],
  licenses: [
    ["license_expired_*", "ntype_license_expired_all"],
    ["license_expiring_*", "ntype_license_expiring_all"],
    ["license_expired_business,license_expiring_business", "ntype_license_business"],
    ["license_expired_civil_defense,license_expiring_civil_defense", "ntype_license_civil_defense"],
  ],
  dayoff: [
    ["dayoff_change", "ntype_dayoff_change"],
    ["dayoff_confirm_note", "ntype_dayoff_confirm_note"],
    ["dayoff_reminder", "ntype_dayoff_reminder"],
    ["dayoff_new_employee", "ntype_dayoff_new_employee"],
  ],
  empmgmt: [
    ["em_request_pending", "ntype_em_request_pending"],
    ["em_request_result", "ntype_em_request_result"],
    ["critical_field", "ntype_critical_field"],
    ["vacation_return_soon", "ntype_vacation_return_soon"],
    ["vacation_overdue", "ntype_vacation_overdue"],
  ],
};

export default function ModuleAlertsPage() {
  const { module } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const meta = notifModule(module);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [debQ, setDebQ] = useState("");
  const [prio, setPrio] = useState("");
  const [ntype, setNtype] = useState("");
  const [days, setDays] = useState("");
  const [sort, setSort] = useState("newest");
  const [busy, setBusy] = useState(false);
  const qTimer = useRef(null);

  useEffect(() => {
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => setDebQ(q), 400);
    return () => clearTimeout(qTimer.current);
  }, [q]);

  const load = useCallback(async (skip = 0, append = false) => {
    setLoading(true);
    try {
      const params = { module, status: tab, skip, limit: 100 };
      if (prio) params.priority = prio;
      if (ntype) params.type = ntype;
      if (debQ.trim()) params.q = debQ.trim();
      if (days) params.days = days;
      const r = await api.get("/notifications", { params });
      setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      setTotal(r.data.total || 0);
      setStats(r.data.stats || {});
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [module, tab, prio, ntype, debQ, days]);

  useEffect(() => { setNtype(""); }, [module]);

  useEffect(() => { load(0, false); }, [load]);

  const sorted = useMemo(() => {
    const arr = [...items];
    if (sort === "oldest") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "priority") arr.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9) || (b.created_at || "").localeCompare(a.created_at || ""));
    else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return arr;
  }, [items, sort]);

  const markRead = async (ids) => {
    try {
      await api.post("/notifications/read", { ids, module });
      setItems((prev) => prev.map((n) => (ids.includes(n.id) && n.status === "new" ? { ...n, is_read: true, status: "read" } : n)));
      setStats((s) => ({ ...s, unread: Math.max(0, (s.unread || 0) - ids.length) }));
    } catch (e) { /* ignore */ }
  };

  const archive = async (ids) => {
    try {
      await api.post("/notifications/archive", { ids, module });
      toast.success(t("notif_archive"));
      load(0, false);
    } catch (e) { /* ignore */ }
  };

  const unarchive = async (ids) => {
    try {
      await api.post("/notifications/unarchive", { ids, module });
      toast.success(t("notif_unarchive"));
      load(0, false);
    } catch (e) { /* ignore */ }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/read", { module });
      toast.success(t("notif_mark_all_read"));
      load(0, false);
    } catch (e) { /* ignore */ }
    setBusy(false);
  };

  const archiveAll = async () => {
    if (!window.confirm(t("notif_archive_all_confirm"))) return;
    setBusy(true);
    try {
      await api.post("/notifications/archive", { module });
      toast.success(t("notif_archive_all"));
      load(0, false);
    } catch (e) { /* ignore */ }
    setBusy(false);
  };

  const exportXlsx = async () => {
    try {
      const res = await fetch(`${API}/notifications/xlsx?module=${module}&status=${tab}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `notifications_${module}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Error"); }
  };

  const openAction = (n) => {
    if (n.status === "new") markRead([n.id]);
    const kind = n.action?.kind;
    if (kind === "open_employee" || n.module === "employees") {
      navigate(`/employees${n.employee_name ? `?q=${encodeURIComponent(n.employee_name)}` : ""}`);
    } else if (kind === "open_licenses" || n.module === "licenses") {
      navigate("/licenses");
    } else if (n.module === "dayoff") {
      navigate("/dayoff");
    } else if (kind === "open_em_employee") {
      navigate("/employee-management/employees");
    } else if (n.module === "empmgmt") {
      navigate("/employee-management/approvals");
    }
  };

  const decideTransfer = async (n, approve) => {
    if (!n.action?.id) return;
    try {
      await api.post(`/transfers/${n.action.id}/${approve ? "approve" : "reject"}`);
      toast.success(approve ? t("notif_approve") : t("notif_reject"));
      load(0, false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const statChips = [
    { key: "unread", label: t("notif_stat_unread"), cls: "bg-rose-50 text-rose-700 border-rose-200" },
    { key: "critical", label: t("notif_prio_critical"), cls: "bg-rose-600 text-white border-rose-600", filter: "critical" },
    { key: "high", label: t("notif_prio_high"), cls: "bg-rose-100 text-rose-700 border-rose-200", filter: "high" },
    { key: "medium", label: t("notif_prio_medium"), cls: "bg-amber-100 text-amber-800 border-amber-200", filter: "medium" },
    { key: "low", label: t("notif_prio_low"), cls: "bg-slate-100 text-slate-600 border-slate-200", filter: "low" },
    { key: "archived", label: t("notif_stat_archived"), cls: "bg-slate-50 text-slate-500 border-slate-200" },
  ];

  const inp = "px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white";

  return (
    <div className="space-y-4" data-testid={`alerts-page-${module}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50" data-testid="alerts-back-btn">
            <ArrowRight size={16} className="rtl:rotate-0 ltr:rotate-180" />
          </button>
          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.grad} text-white flex items-center justify-center`}>
            <meta.icon size={20} />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-800">{t(meta.titleKey)}</h1>
            <p className="text-xs text-slate-400">{t(meta.descKey)}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => load(0, false)} className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50" data-testid="alerts-refresh-btn">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {t("notif_refresh")}
          </button>
          <button onClick={exportXlsx} className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow hover:opacity-90" data-testid="alerts-export-btn">
            <FileSpreadsheet size={13} /> {t("notif_export")}
          </button>
          {tab === "active" && (
            <>
              <button onClick={markAllRead} disabled={busy || !(stats.unread > 0)} className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100 disabled:opacity-40" data-testid="alerts-mark-all-read-btn">
                <CheckCheck size={13} /> {t("notif_mark_all_read")}
              </button>
              <button onClick={archiveAll} disabled={busy || items.length === 0} className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-40" data-testid="alerts-archive-all-btn">
                <Archive size={13} /> {t("notif_archive_all")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-2" data-testid="alerts-stats-row">
        {statChips.map((c) => (
          <button
            key={c.key}
            onClick={() => c.filter && setPrio(prio === c.filter ? "" : c.filter)}
            className={`px-3 py-1.5 rounded-full border text-[11px] font-black ${c.cls} ${c.filter && prio === c.filter ? "ring-2 ring-offset-1 ring-teal-400" : ""} ${c.filter ? "cursor-pointer" : "cursor-default"}`}
            data-testid={`alerts-stat-${c.key}`}
          >
            {c.label}: {stats[c.key] ?? 0}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-xl overflow-hidden border border-slate-200">
          {["active", "archived"].map((tb) => (
            <button key={tb} onClick={() => setTab(tb)}
              className={`px-4 py-2 text-xs font-black transition ${tab === tb ? "bg-teal-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              data-testid={`alerts-tab-${tb}`}>
              {tb === "active" ? t("notif_tab_active") : t("notif_tab_archived")}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-slate-300" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("notif_search_ph")}
            className={`${inp} w-full ps-9`} data-testid="alerts-search-input" />
        </div>
        {(TYPE_OPTIONS[module] || []).length > 0 && (
          <select value={ntype} onChange={(e) => setNtype(e.target.value)} className={inp} data-testid="alerts-type-filter">
            <option value="">{t("ntype_all")}</option>
            {TYPE_OPTIONS[module].map(([val, key]) => (
              <option key={val} value={val}>{t(key)}</option>
            ))}
          </select>
        )}
        <select value={prio} onChange={(e) => setPrio(e.target.value)} className={inp} data-testid="alerts-priority-filter">
          <option value="">{t("notif_prio_all")}</option>
          <option value="critical">{t("notif_prio_critical")}</option>
          <option value="high">{t("notif_prio_high")}</option>
          <option value="medium">{t("notif_prio_medium")}</option>
          <option value="low">{t("notif_prio_low")}</option>
        </select>
        <select value={days} onChange={(e) => setDays(e.target.value)} className={inp} data-testid="alerts-days-filter">
          <option value="">{t("notif_days_all")}</option>
          <option value="7">{t("notif_days_7")}</option>
          <option value="30">{t("notif_days_30")}</option>
          <option value="90">{t("notif_days_90")}</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={inp} data-testid="alerts-sort-select">
          <option value="newest">{t("notif_sort_newest")}</option>
          <option value="oldest">{t("notif_sort_oldest")}</option>
          <option value="priority">{t("notif_sort_priority")}</option>
        </select>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="alerts-list">
        {!loading && sorted.length === 0 && (
          <div className="text-center text-sm text-slate-400 bg-white border border-slate-200 rounded-2xl py-10" data-testid="alerts-empty">
            {tab === "archived" ? t("notif_empty_archived") : t("notif_empty")}
          </div>
        )}
        {sorted.map((n) => (
          <div key={n.id}
            className={`bg-white rounded-2xl border p-4 flex flex-col gap-2 transition ${n.status === "new" ? "border-rose-200 shadow-sm" : "border-slate-200"}`}
            data-testid={`alert-item-${n.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.low}`}>
                  {t(`notif_prio_${n.priority}`) || n.priority}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${STATUS_STYLES[n.status] || STATUS_STYLES.read}`} data-testid={`alert-status-${n.id}`}>
                  {t(`notif_status_${n.status}`) || n.status}
                </span>
                <span className="text-[10px] text-slate-400">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {tab === "active" && n.status === "new" && (
                  <button onClick={() => markRead([n.id])} title={t("notif_mark_read")}
                    className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 border border-transparent hover:border-teal-200"
                    data-testid={`alert-read-btn-${n.id}`}>
                    <Check size={14} />
                  </button>
                )}
                {tab === "active" ? (
                  <button onClick={() => archive([n.id])} title={t("notif_archive")}
                    className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 border border-transparent hover:border-slate-200"
                    data-testid={`alert-archive-btn-${n.id}`}>
                    <Archive size={14} />
                  </button>
                ) : (
                  !n.resolved_at && (
                    <button onClick={() => unarchive([n.id])} title={t("notif_unarchive")}
                      className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 border border-transparent hover:border-slate-200"
                      data-testid={`alert-unarchive-btn-${n.id}`}>
                      <ArchiveRestore size={14} />
                    </button>
                  )
                )}
                <button onClick={() => openAction(n)} title={t("notif_open_action")}
                  className="p-1.5 rounded-lg text-sky-600 hover:bg-sky-50 border border-transparent hover:border-sky-200"
                  data-testid={`alert-open-btn-${n.id}`}>
                  <ExternalLink size={14} />
                </button>
              </div>
            </div>
            <div className={`text-sm font-bold ${n.status === "new" ? "text-slate-900" : "text-slate-600"}`}>{n.title}</div>
            {n.body && <div className="text-xs text-slate-500">{n.body}</div>}
            <div className="flex items-center gap-3 text-[11px] text-slate-400 flex-wrap">
              {n.employee_name && <span>👤 {n.employee_name}</span>}
              {n.branch_name && <span>🏬 {n.branch_name}</span>}
              {n.actor_name && <span>✍️ {n.actor_name}</span>}
              {n.resolved_at && <span className="text-emerald-600 font-bold">✓ {t("notif_action_done")}{n.resolved_note ? ` — ${n.resolved_note}` : ""}</span>}
            </div>
            {n.type === "transfer_request" && n.action?.id && !n.resolved_at && tab === "active" && (
              <div className="flex gap-2 pt-1">
                <button onClick={() => decideTransfer(n, true)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-black text-white bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`alert-approve-btn-${n.id}`}>
                  ✅ {t("notif_approve")}
                </button>
                <button onClick={() => decideTransfer(n, false)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-black text-rose-700 bg-rose-100 hover:bg-rose-200"
                  data-testid={`alert-reject-btn-${n.id}`}>
                  ❌ {t("notif_reject")}
                </button>
              </div>
            )}
          </div>
        ))}
        {items.length < total && (
          <button onClick={() => load(items.length, true)} disabled={loading}
            className="w-full py-2.5 rounded-2xl border border-slate-200 bg-white text-sm font-bold text-slate-500 hover:bg-slate-50"
            data-testid="alerts-load-more-btn">
            {t("notif_load_more")} ({items.length}/{total})
          </button>
        )}
      </div>
    </div>
  );
}
